import { Select } from '@ariakit/react/select';
import cn from 'classnames';
import { Children, cloneElement, isValidElement } from 'react';
import { SelectButton } from '#components/atoms/buttons/select-button/SelectButton.tsx';
import { HelpTip } from '#components/atoms/forms/help-tip/HelpTip.tsx';
import { Input, Label, MFAInput, Textarea } from '#components/atoms/forms/index.ts';
import { useGuaranteedId } from '#hooks/useGuaranteedId.ts';
import styles from './Field.module.css';

const IDABLE_TYPES = [Input, Textarea, 'input', 'select', Select, SelectButton, MFAInput];

type Props = {
  /** If an id is not provided, one will be generated with the format `FieldX` where X auto-increments. */
  id?: string;
  /** String labels are wrapped in a <Label> and ids are associated automatically.  Alternatively, a <Label> can be provided directly. */
  label?: React.ReactNode;
  /** If a string label is provided, a helpTip can be added using this prop */
  helpTip?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Setting to true removes the label with display: none and sets hidden on the form element */
  hidden?: boolean;
};

/**
 * The Field component provides an easy way to render a form element with a label.
 *
 * If an id prop is provided, it will be used for the label element (if also provided), as well
 * as assigned to any children this component considers to be `id-able`.  See IDABLE_TYPES
 * for a list of these, and add to them as needed.
 *
 * Also, if an ErrorMessage or HintText is included, the Field will generate an id for it, and give
 * the control element an `aria-describedby` automatically.
 */
export function Field({ id: explicitFieldId, label, helpTip, hidden, className, children }: Props) {
  const fieldId = useGuaranteedId({ prefix: 'Field', id: explicitFieldId });
  const helpTextId = useGuaranteedId({ prefix: 'help-text' });
  const hintTextId = useGuaranteedId({ prefix: 'hint-text' });
  const errorMessageId = useGuaranteedId({ prefix: 'error-message' });
  const describingIdMap: Record<string, string> = {
    HelpText: helpTextId,
    HintText: hintTextId,
    ErrorMessage: errorMessageId,
  };

  // Keep track of the ids that we assign as descriptors
  const descriptorIds: string[] = [];

  // We need to add an `aria-describedby` linking to error messages or hint text.  Collect the ids of any such elements.
  const childrenWithDescriptorIds: React.ReactNode = Children.map(children, (child) => {
    if (isValidElement<{ id?: string }>(child)) {
      const componentType =
        typeof child.type === 'function' &&
        'displayName' in child.type &&
        typeof child.type.displayName === 'string' &&
        child.type.displayName;

      const descriptorId = componentType && describingIdMap[componentType];
      // Add the id, but don't overwrite an existing id
      if (descriptorId && !child.props.id) {
        descriptorIds.push(descriptorId);
        return cloneElement(child, { id: descriptorId });
      }
    }
    return child;
  });

  /**
   * Recursive function to dig through react children looking for an `IDABLE_TYPE` that can be treated as
   * the base field control.  We'll assign it an `id` attribute if it doesn't already have one
   * (to reference from the label), and an ariaDescribedBy if possible.
   */
  function applyAttributes(child: React.ReactNode): React.ReactNode {
    //
    if (
      isValidElement<{ id?: string; 'aria-describedby': string; hidden?: boolean; children?: React.ReactNode }>(child)
    ) {
      // First look to see if it's a type that we can add an aria-describedby to.
      // @ts-expect-error poor refinement?  child.type is safe to access after checking it's a valid element
      if (IDABLE_TYPES.includes(child.type)) {
        // Use explicit aria-describedby, as well as generated if there's a descriptor
        const ariaDescribedby = cn(child.props['aria-describedby'], descriptorIds);
        return cloneElement(child, {
          // Do not overwrite an existing ID
          id: child.props.id ?? fieldId,
          // If ariaDescribedby is falsey (e.g. an empty string), don't set aria-describedby at all
          'aria-describedby': ariaDescribedby || undefined,
          // Hide the child if needed
          hidden,
        });
      }

      // If we didn't find one, but there are children, recurse through those
      if (Array.isArray(child.props.children) && child.props.children.length) {
        return cloneElement(child, {
          children: Children.map<React.ReactNode, React.ReactNode>(child.props.children, (subchild) =>
            applyAttributes(subchild)
          ),
        });
      }

      // Still no luck, so return what we've got
      return child;
    }

    // If it wasn't a react element, return it
    return child;
  }

  const clonedChildren = Children.map(childrenWithDescriptorIds, (child) => {
    return applyAttributes(child);
  });

  return (
    <div className={cn(styles.Field, className, { [styles.Field___hidden]: hidden })}>
      {label && typeof label === 'string' ?
        <Label htmlFor={fieldId} className={cn({ flex: helpTip, 'align-center': helpTip })}>
          {label}
          {helpTip ?
            <HelpTip>{helpTip}</HelpTip>
          : null}
        </Label>
      : label}
      {clonedChildren}
    </div>
  );
}
